
import React, { useState, useRef, useEffect } from 'react';
import { Search, ShoppingBag, Menu, X, Sun, Moon, ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { NAV_LINKS } from '../../data/constants';
import { BRANDS } from '../../data/brands';
import { useScroll } from '../../hooks/useScroll';
import { useTheme, useBrand } from '../../context/ColorContext';

const Navbar = ({ onOpenVisualizer, onOpenSearch }) => {
    const scrolled = useScroll(50);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isBrandMenuOpen, setIsBrandMenuOpen] = useState(false);
    const brandMenuRef = useRef(null);
    const timeoutRef = useRef(null);
    const { theme, toggleTheme } = useTheme();
    const { activeBrand, switchBrand } = useBrand();

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (brandMenuRef.current && !brandMenuRef.current.contains(event.target)) {
                setIsBrandMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleMouseEnter = () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setIsBrandMenuOpen(true);
    };

    const handleMouseLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsBrandMenuOpen(false);
        }, 2000);
    };

    const isDark = theme === 'dark';

    const visualizerText = activeBrand.id === 'dufa' ? 'Odanı Canlandır' :
        activeBrand.id === 'genc' ? 'Mobilyanı Yenile' :
            'Ahşabını Koru';

    return (
        <>
            <nav
                className={`fixed top-0 left-0 right-0 z-[70] transition-all duration-300 border-b ${scrolled
                    ? (isDark ? 'bg-black/40 backdrop-blur-xl border-white/10 py-4' : 'bg-white/70 backdrop-blur-xl border-black/5 py-4')
                    : 'bg-transparent border-transparent py-6'
                    }`}
            >
                <div className="container mx-auto px-6 flex justify-between items-center relative">
                    {/* Left Side: Brand Selector & Logo */}
                    <div className="flex items-center gap-8 z-10">


                        {/* Logo Link */}
                        <Link
                            to="/"
                            className="hidden lg:flex items-center gap-2 group cursor-pointer"
                        >
                            <img
                                src={activeBrand.logo}
                                alt={activeBrand.name}
                                className="h-[80px] md:h-[110px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                            />
                        </Link>
                    </div>

                    {/* Mobile Only: Centered Logo */}
                    <Link
                        to="/"
                        className="lg:hidden absolute left-1/2 -translate-x-1/2 flex items-center gap-2 group cursor-pointer z-10"
                    >
                        <img
                            src={activeBrand.logo}
                            alt={activeBrand.name}
                            className="h-[80px] md:h-[110px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                            style={{ filter: isDark ? 'drop-shadow(1px 0 0 #fff) drop-shadow(-1px 0 0 #fff) drop-shadow(0 1px 0 #fff) drop-shadow(0 -1px 0 #fff)' : undefined }}
                        />
                    </Link>

                    {/* Right Side: Menu & Actions */}
                    <div className="flex items-center gap-4 lg:gap-8 z-10">
                        {/* Desktop Menu */}
                        <div className="hidden lg:flex items-center gap-8">
                            {NAV_LINKS.map((link) => {
                                const paths = {
                                    "ÜRÜNLER": `/urunler`,
                                    "KARTELALAR": `/kartelalar`,
                                    "İLHAM": `/ilham`,
                                    "BAYİLER": `/bayiler`,
                                    "ÜRETİM": `/uretim`,
                                    "HAKKIMIZDA": `/hakkimizda`,
                                    "İLETİŞİM": `/iletisim`
                                };
                                const toPath = paths[link] || "#";

                                return (
                                    <Link
                                        key={link}
                                        to={toPath}
                                        className={`text-sm font-bold tracking-widest transition-all hover:scale-105 transform duration-200 ${isDark ? 'text-white/70 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                                    >
                                        {link}
                                    </Link>
                                );
                            })}
                        </div>

                        {/* Actions */}
                        <div className="hidden md:flex items-center gap-4">
                            <button
                                onClick={toggleTheme}
                                className={`p-2 transition-all duration-300 rounded-full ${isDark ? 'text-white/70 hover:text-white bg-white/5' : 'text-slate-600 hover:text-slate-900 bg-black/5'}`}
                            >
                                {isDark ? <Sun size={20} /> : <Moon size={20} />}
                            </button>
                            <button
                                onClick={onOpenSearch}
                                className={`p-2 transition ${isDark ? 'text-white/70 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
                            >
                                <Search size={20} />
                            </button>
                            <a
                                href="https://www.kayalarboya.com.tr"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`px-6 py-2 rounded-full font-bold text-xs uppercase tracking-widest transition-all shadow-lg active:scale-95 ${isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-black/5 hover:bg-black/10 text-slate-900'
                                    }`}
                            >
                                Online Mağaza
                            </a>
                        </div>

                        {/* Mobile Toggle */}
                        <button
                            className={`lg:hidden ${isDark ? 'text-white' : 'text-slate-900'}`}
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        >
                            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
                <div className={`fixed inset-0 z-[100] flex flex-col items-center py-12 md:justify-center gap-6 md:gap-8 lg:hidden overflow-y-auto ${isDark ? 'bg-slate-950 text-white' : 'bg-white text-slate-900'} animate-in fade-in duration-300`}>
                    <button
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`absolute top-6 right-6 p-2 rounded-full ${isDark ? 'bg-white/10 text-white' : 'bg-black/5 text-slate-900'}`}
                    >
                        <X size={28} />
                    </button>



                    {NAV_LINKS.map((link) => {
                        const paths = {
                            "ÜRÜNLER": `/urunler`,
                            "KARTELALAR": `/kartelalar`,
                            "İLHAM": `/ilham`,
                            "BAYİLER": `/bayiler`,
                            "ÜRETİM": `/uretim`,
                            "HAKKIMIZDA": `/hakkimizda`,
                            "İLETİŞİM": `/iletisim`
                        };
                        const toPath = paths[link] || "#";
                        return (
                            <Link
                                key={link}
                                to={toPath}
                                onClick={() => setIsMobileMenuOpen(false)}
                                className="text-xl sm:text-2xl font-black uppercase tracking-[0.2em] transform transition-transform active:scale-95"
                            >
                                {link}
                            </Link>
                        );
                    })}

                    <div className="flex flex-col items-center gap-4 mt-4 w-full px-8">
                        <a
                            href="https://www.kayalarboya.com.tr"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full py-4 text-center text-white rounded-xl font-black uppercase tracking-widest shadow-xl active:scale-95 transition-transform text-sm"
                            style={{ backgroundColor: activeBrand.primaryColor }}
                        >
                            Online Mağaza
                        </a>
                        <button
                            onClick={toggleTheme}
                            className={`p-4 rounded-full ${isDark ? 'bg-white/10' : 'bg-black/5'}`}
                        >
                            {isDark ? <Sun size={24} /> : <Moon size={24} />}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default Navbar;
